// What the service actually pushed, for troubleshooting a location.
//
// A table, because the useful part of a posting is a handful of facts and a
// verdict, and those compare down a column far better than they read as a
// sentence per row. The verbatim line does not fit that shape — it is
// nineteen comma-separated fields — so it lives in the row's expanded panel:
// still one click from every row, without doubling the height of all of them.
//
// Only failures are coloured. A green tick per normal line would make ordinary
// traffic look like an event, and the reason to open this is to find the lines
// that are wrong. A failure keeps its message in the row rather than behind
// the chevron for the same reason: it is what the reader came for.

import { DataTable, type DataTableColumn } from "@pej/kore/components";
import { usePostings } from "@/api/postings";
import type { Posting, StreamInfo } from "@/api/types";

/**
 * Which of the four things this panel can say.
 *
 * Named and tested on its own because the interesting case is invisible in the
 * happy path: a query that is neither loading nor errored is NOT necessarily
 * loaded. Between react-query's retries it is pending with nothing in flight,
 * so `isLoading` (which is `isPending && isFetching`) and `isError` are both
 * false. Branching on those two alone read that gap as "loaded, and empty" —
 * telling the reader the service had never posted when a request had merely
 * failed once, and sending them to check a POS terminal that was fine.
 *
 * So `empty` is reachable only from an actual success. Anything else that is
 * not a settled failure is "we don't know yet".
 */
export function feedState(query: {
    isError: boolean;
    isSuccess: boolean;
    data: { items: unknown[] } | undefined;
}): "error" | "loading" | "list" | "empty" {
    if (query.isError) return "error";
    if (!query.isSuccess || !query.data) return "loading";
    return query.data.items.length > 0 ? "list" : "empty";
}

/** Every fact column is blank on a line that never became an order. */
const NONE = <span className="text-kore-text-faint">—</span>;

function ResultCell({ posting }: { posting: Posting }) {
    const { verdict } = posting;
    if (verdict.kind === "failed") {
        return <span className="text-kore-danger-text">{verdict.message}</span>;
    }
    if (verdict.kind === "skipped") {
        return <span className="text-kore-text-muted">Not an order</span>;
    }
    return <span className="text-kore-text-muted">Order</span>;
}

const columns: DataTableColumn<Posting>[] = [
    {
        key: "Time",
        header: "Time",
        width: "7rem",
        cell: (posting) => (
            <time dateTime={posting.receivedAt} className="tabular-nums">
                {new Date(posting.receivedAt).toLocaleTimeString()}
            </time>
        ),
    },
    // Widest by a distance: on a failed line it carries the parser's message,
    // which names the field it choked on.
    { key: "Result", header: "Result", cell: (posting) => <ResultCell posting={posting} /> },
    {
        key: "Order",
        header: "Order",
        width: "9rem",
        cell: (posting) =>
            posting.verdict.kind === "parsed" ? <span className="tabular-nums">{posting.verdict.orderId}</span> : NONE,
    },
    {
        key: "Device",
        header: "Device",
        width: "6rem",
        cell: (posting) => (posting.verdict.kind === "parsed" ? posting.verdict.deviceId : NONE),
    },
    {
        key: "Operator",
        header: "Operator",
        width: "12rem",
        cell: (posting) => (posting.verdict.kind === "parsed" ? posting.verdict.operatorName : NONE),
    },
    {
        key: "Waited",
        header: "Waited",
        width: "6rem",
        align: "right",
        cell: (posting) =>
            posting.verdict.kind === "parsed" ? (
                <span className="tabular-nums">{posting.verdict.durationMinutes} min</span>
            ) : (
                NONE
            ),
    },
];

export function PostingsCard({ stream }: { stream: StreamInfo }) {
    const postings = usePostings(stream.streamId);
    const feed = postings.data;
    const state = feedState(postings);

    return (
        // No Card around it: DataTable already renders a bordered, rounded
        // surface of its own, and wrapping it in another stacked two borders
        // with a hairline above and below the rows. The list pages have said
        // so since locations.index.tsx — the table is a sibling of whatever
        // sits beside it, never nested. The heading goes in the table's own
        // toolbar row, which is the surface it labels.
        <div className="flex flex-col gap-2" data-testid="postings">
            <DataTable<Posting>
                caption="Recent postings"
                density="compact"
                toolbar={
                    <>
                        <h2 className="text-base font-semibold">Recent postings</h2>
                        {feed ? (
                            <span className="text-sm text-kore-text-muted">{feed.lastHourCount} in the last hour</span>
                        ) : null}
                    </>
                }
                columns={columns}
                rows={feed?.items ?? []}
                rowKey={(posting) => String(posting.id)}
                // All four states go through, "error" included: leaving it
                // undefined would fall back to the empty state, which is the
                // exact confusion `feedState` exists to prevent.
                state={state === "list" ? "populated" : state}
                error={{ title: "Couldn't load what the posting service pushed", detail: "Retrying…" }}
                // Said plainly, because an empty feed here is a setup problem
                // and a bare "no rows" would leave the reader nowhere to go.
                empty={{
                    headline: "Nothing has arrived yet.",
                    body: "Check that the posting service is installed on the POS terminal, that its service account matches this location, and that the posting file path is right.",
                }}
                renderExpanded={(posting) => (
                    <div className="flex flex-col gap-1">
                        {/* Verbatim, and scrolling in its own track: wrapping
                            nineteen fields turns the columns into mush. */}
                        <code className="block overflow-x-auto whitespace-pre text-xs text-kore-text-muted">
                            {posting.line}
                        </code>
                        {posting.batchId ? (
                            <span className="text-xs text-kore-text-faint">Batch {posting.batchId}</span>
                        ) : null}
                    </div>
                )}
            />
            {/* Outside the table, unbordered: a caption on the surface above,
                not another row hanging off it. */}
            {state === "list" && feed ? (
                <p className="px-1 text-xs text-kore-text-muted">Lines are kept for {feed.retentionDays} days.</p>
            ) : null}
        </div>
    );
}
